import { AuthContext } from '@/context/authContext'
import {
  checkActionCode,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  getAuth,
  sendPasswordResetEmail as sendPasswordResetEmailFromFirebase,
  signInWithEmailAndPassword,
  signOut,
  setPersistence,
  browserSessionPersistence,
  User,
} from 'firebase/auth'
import { FirebaseError } from 'firebase/app'
import { useCallback, useContext, useEffect, useState } from 'react'
import firebaseApp from '@/lib/firebase/firebaseConfig'
import * as Sentry from '@sentry/nextjs'

interface CustomClaims {
  legacy_shopify_user?: boolean
  scope?: Array<string>
  zoe_user_role?: string
  zoe_username?: string
}

export interface AuthResult {
  user: User | null
  error: string | null
}

const FIREBASE_AUTH_ERROR_MESSAGES: Record<string, string> = {
  'auth/email-already-in-use': 'An account with this email already exists.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/weak-password': 'Password is too weak. Please choose a stronger password.',
  'auth/operation-not-allowed': 'Email/password accounts are not enabled. Please contact support.',
  'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
  'auth/network-request-failed': 'Network error. Please check your connection and try again.',
  'auth/user-not-found': 'No account found with this email address.',
  'auth/wrong-password': 'Incorrect password. Please try again.',
  'auth/user-disabled': 'This account has been disabled. Please contact support.',
  'auth/invalid-credential': 'Invalid email or password.',
  'auth/account-exists-with-different-credential':
    'An account already exists with the same email address but different sign-in credentials.',
  'auth/requires-recent-login': 'This operation requires recent authentication. Please log in again.',
  'auth/invalid-action-code': 'The password reset link is invalid or has expired.',
  'auth/expired-action-code': 'The password reset link has expired.',
  'auth/invalid-verification-code': 'The verification code is invalid.',
  'auth/invalid-verification-id': 'The verification ID is invalid.',
}

const getFirebaseAuthErrorMessage = (error: FirebaseError): string => {
  const userFriendlyMessage = FIREBASE_AUTH_ERROR_MESSAGES[error.code]

  if (userFriendlyMessage) {
    return userFriendlyMessage
  }

  console.error('Unhandled Firebase auth error:', error.code, error.message)
  return 'An error occurred. Please try again.'
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  const { user, setUser } = context
  const [customClaims, setCustomClaims] = useState<CustomClaims>({})

  const fetchCustomClaims = useCallback(async (currentUser: User) => {
    try {
      const idTokenResult = await currentUser.getIdTokenResult(true)
      setCustomClaims(idTokenResult.claims as CustomClaims)
    } catch (error) {
      console.error('Error fetching custom claims:', error)
      setCustomClaims({})
      Sentry.captureException(error)
    }
  }, [])

  useEffect(() => {
    if (user) {
      fetchCustomClaims(user)
    } else {
      setCustomClaims({})
    }
  }, [user, fetchCustomClaims])

  const login = async (email: string, password: string) => {
    let error = null

    try {
      const auth = getAuth(firebaseApp)
      await setPersistence(auth, browserSessionPersistence)
      const userCredential = await signInWithEmailAndPassword(auth, email, password)
      setUser(userCredential.user)
      return { user: userCredential.user, error: error }
    } catch (error) {
      console.error(error)
      // Handle error here
      return { user: null, error: error as string }
    }
  }

  const logout = async () => {
    try {
      const auth = getAuth(firebaseApp)
      await signOut(auth)
      setUser(null)
      return { user: null, error: null }
    } catch (error) {
      console.error(error)
      // Handle error here
      return { user: null, error: error as string }
    }
  }

  const signup = async (email: string, password: string): Promise<AuthResult> => {
    try {
      const auth = getAuth(firebaseApp)
      await setPersistence(auth, browserSessionPersistence)
      const userCredential = await createUserWithEmailAndPassword(auth, email, password)
      setUser(userCredential.user)
      return { user: userCredential.user, error: null }
    } catch (error) {
      let errorMessage = 'An error occurred. Please try again.'
      if (error instanceof FirebaseError) {
        errorMessage = getFirebaseAuthErrorMessage(error)
      }
      return { user: null, error: errorMessage }
    }
  }

  const sendPasswordResetEmail = async (email: string) => {
    try {
      const auth = getAuth(firebaseApp)
      await sendPasswordResetEmailFromFirebase(auth, email)
      return { user: user, error: null }
    } catch (error) {
      console.error(error)
      // Handle error here
      return { user: user, error: error as string }
    }
  }

  const resetPassword = async (oobCode: string, newPassword: string) => {
    try {
      const auth = getAuth(firebaseApp)
      await confirmPasswordReset(auth, oobCode, newPassword)
      return { user: user, error: null }
    } catch (error) {
      console.error(error)
      // Handle error here
      return { user: user, error: error as string }
    }
  }

  const checkOobCodeAndGetEmail = async (oobCode: string) => {
    if (oobCode === '') {
      return { email: undefined, error: 'Invalid oobCode' }
    }

    try {
      const auth = getAuth(firebaseApp)
      const actionCodeInfo = await checkActionCode(auth, oobCode)
      const email = actionCodeInfo.data.email ?? undefined
      return { email: email, error: null }
    } catch (error) {
      console.error(error)
      return { email: undefined, error: 'Invalid oobCode' }
    }
  }

  const isLegacyShopifyUser = () => {
    return customClaims.legacy_shopify_user ?? false
  }

  const isFreemiumUser = () => {
    return (customClaims.zoe_user_role === 'FREE' || customClaims.zoe_user_role === 'ZOE_PLUS') ?? false
  }

  return {
    user,
    login,
    logout,
    signup,
    sendPasswordResetEmail,
    resetPassword,
    checkOobCodeAndGetEmail,
    isLegacyShopifyUser,
    isFreemiumUser,
  }
}
